import React, { useState, useId } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { type DiscountTier } from '../../containers/Cart/Checkout/helpers/useCartProduct';
import findActiveTier from './helpers';

import './style.scss';

interface DiscountTierAccordionProps {
  tiers: DiscountTier[];
  /** Current quantity in the cart; highlights the matching tier and drives the summary. */
  activeQty?: number;
  /** Colour scheme: 'default' (peach, checkout) or 'product' (green, product block). */
  variant?: 'default' | 'product';
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const rangeLabel = (tier: DiscountTier, t: TranslateFn): string => (tier.max_qty !== null
  ? t('checkout.discount_accordion_range', {
    min: tier.min_qty,
    max: tier.max_qty,
  })
  : t('checkout.discount_accordion_min', { min: tier.min_qty }));

/**
 * Collapsible volume-discount ("staffelkorting") price list. Shows the active
 * tier in its header and the full tier table when expanded. Reused across the
 * checkout product cards (v1 cart + v2 checkout); driven purely by props so it
 * carries no cart wiring of its own.
 */
const DiscountTierAccordion: React.FC<DiscountTierAccordionProps> = ({
  tiers,
  activeQty,
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const listId = useId();

  if (tiers.length === 0) {
    return null;
  }

  const activeTier = findActiveTier(tiers, activeQty);

  return (
    <div
      className={classnames(
        'DiscountTierAccordion',
        `DiscountTierAccordion--${variant}`,
        {
          'DiscountTierAccordion--open': open,
        },
      )}
    >
      <button
        type="button"
        className="DiscountTierAccordion__header"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="DiscountTierAccordion__summary">
          {activeTier ? (
            <>
              <span className="DiscountTierAccordion__dot" aria-hidden="true" />
              <strong className="DiscountTierAccordion__summary-label">
                {t('checkout.discount_accordion_active', {
                  percentage: activeTier.percentage,
                })}
              </strong>
              <span className="DiscountTierAccordion__summary-range">
                {rangeLabel(activeTier, t)}
              </span>
            </>
          ) : (
            <strong className="DiscountTierAccordion__summary-label">
              {t('checkout.discount_accordion_available')}
            </strong>
          )}
        </span>
        <span className="DiscountTierAccordion__toggle">
          {open && (
            <span className="DiscountTierAccordion__toggle-label">
              {t('checkout.discount_accordion_close')}
            </span>
          )}
          <span
            className="icon icon-chevron-right DiscountTierAccordion__chevron"
            aria-hidden="true"
          />
        </span>
      </button>

      {/* Always rendered; the wrapper animates its height open/closed. */}
      <div
        id={listId}
        className="DiscountTierAccordion__list-wrapper"
        aria-hidden={!open}
      >
        <div className="DiscountTierAccordion__list-inner">
          <ul className="DiscountTierAccordion__list">
            {tiers.map((tier) => {
              const isActive = tier === activeTier;
              return (
                <li
                  key={`${tier.min_qty}-${tier.max_qty ?? 'max'}`}
                  className={classnames('DiscountTierAccordion__row', {
                    'DiscountTierAccordion__row--active': isActive,
                  })}
                >
                  <span className="DiscountTierAccordion__range">
                    {rangeLabel(tier, t)}
                    {isActive && (
                      <span className="DiscountTierAccordion__badge">
                        {t('checkout.discount_tier_active')}
                      </span>
                    )}
                  </span>
                  <span className="DiscountTierAccordion__percentage">
                    {t('checkout.discount_accordion_percentage', {
                      percentage: tier.percentage,
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DiscountTierAccordion;
